import { isWaitable, PlaybackError } from './errors';

export interface WaitForLiveOptions {
  /** Overall budget before giving up with a TIMEOUT error. Default: unbounded. */
  timeoutMs?: number;
  /** Minimum delay between polls. Floored at 1000ms (the server's min / limiter). Default: 1000. */
  floorMs?: number;
  /**
   * Maximum delay between polls in the IMMINENT window (≤ {@link imminentMs}
   * before kickoff). Far-from-kickoff polls are widened automatically using
   * `kickoffAt` (see below) up to {@link sanityFloorMs}, regardless of this
   * value — so this knob really controls only the near-kickoff cadence. Default: 30000.
   */
  ceilMs?: number;
  /**
   * Caller-supplied scheduled kickoff time (epoch ms, or an ISO string), used to
   * widen the long-tail poll cadence so the SDK does not hammer the per-viewer
   * `/v1/playback` endpoint when a viewer is armed hours early.
   *
   * Far-from-kickoff (time-to-kickoff > {@link imminentMs}) the poll interval is
   * stretched to `min(ttk / 10, sanityFloorMs)` (never tighter than the server's
   * `Retry-After`, never longer than {@link sanityFloorMs}, never longer than
   * the time remaining to kickoff). Inside the imminent window the cadence
   * collapses to the tight `[floorMs, ceilMs]` range so a 200 is caught fast.
   *
   * When omitted, the SDK also reads `liveStartsAt` off the 425 body if the
   * server returns it (`PlaybackError.liveStartsAtMs`), so newer servers tune
   * automatically without any client change.
   */
  kickoffAt?: number | string;
  /**
   * Threshold (ms) for "imminent kickoff" — inside this window the cadence
   * collapses to the tight `[floorMs, ceilMs]` range. Default: 10 minutes.
   */
  imminentMs?: number;
  /**
   * Absolute hard ceiling on any single wait, even when far from kickoff or
   * when the server returns a very large `Retry-After`. Sanity floor so an
   * early go-live or a schedule change is caught within bounded latency.
   * Default: 5 minutes (300_000ms).
   */
  sanityFloorMs?: number;
  /** Add up to +15% jitter to each delay (never reduces below the server's ask). Default: true. */
  jitter?: boolean;
  /** Called before each wait with the upcoming retry. */
  onState?: (state: WaitState) => void;
}

export type WaitForLive = boolean | WaitForLiveOptions;

export interface WaitState {
  phase: 'tooEarly' | 'unavailable' | 'rateLimited' | 'network';
  /** How long until the next poll, in ms. */
  retryInMs: number;
  /** 1-based retry count. */
  attempt: number;
  /** Wall-clock ms since the wait started. */
  elapsedMs: number;
  /**
   * Time-to-kickoff (ms) used to compute this delay, if known. `undefined` when
   * no kickoff hint is available (caller didn't pass `kickoffAt` and the server
   * didn't carry `liveStartsAt` on the error).
   */
  ttkMs?: number;
}

interface NormalizedWait {
  timeoutMs?: number;
  floorMs: number;
  ceilMs: number;
  imminentMs: number;
  sanityFloorMs: number;
  /** Caller-supplied kickoff (epoch ms), or undefined. */
  kickoffMs?: number;
  jitter: boolean;
  onState?: (state: WaitState) => void;
}

const DEFAULT_FLOOR_MS = 1000;
const DEFAULT_CEIL_MS = 30_000;
const DEFAULT_IMMINENT_MS = 10 * 60 * 1000; // 10 min — collapse to tight cadence
const DEFAULT_SANITY_FLOOR_MS = 5 * 60 * 1000; // 5 min — hard cap on any single sleep
const SERVER_MIN_MS = 1000; // never poll /v1/playback faster than this (per-IP limiter)

const PHASE: Record<string, WaitState['phase']> = {
  TOO_EARLY: 'tooEarly',
  UNAVAILABLE: 'unavailable',
  RATE_LIMITED: 'rateLimited',
  NETWORK: 'network',
};

function normalize(cfg: WaitForLive): NormalizedWait {
  const o: WaitForLiveOptions = cfg === true || cfg === false ? {} : cfg;
  const floorMs = Math.max(SERVER_MIN_MS, o.floorMs ?? DEFAULT_FLOOR_MS);
  const ceilMs = Math.max(floorMs, o.ceilMs ?? DEFAULT_CEIL_MS);
  const imminentMs = Math.max(0, o.imminentMs ?? DEFAULT_IMMINENT_MS);
  const sanityFloorMs = Math.max(ceilMs, o.sanityFloorMs ?? DEFAULT_SANITY_FLOOR_MS);
  return {
    timeoutMs: o.timeoutMs,
    floorMs,
    ceilMs,
    imminentMs,
    sanityFloorMs,
    kickoffMs: parseKickoff(o.kickoffAt),
    jitter: o.jitter ?? true,
    onState: o.onState,
  };
}

function parseKickoff(v: number | string | undefined): number | undefined {
  if (v == null) return undefined;
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : undefined;
}

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

function addJitter(delay: number): number {
  // Only ADDS time, so we never poll faster than the server asked.
  return Math.round(delay * (1 + 0.15 * Math.random()));
}

/**
 * Compute the next poll delay, given the server's hint, an optional kickoff
 * time, and the caller's bounds.
 *
 * The rule:
 *  1. **Server `Retry-After` is the floor.** If the server says "wait 2s," we
 *     never poll sooner, regardless of how far away kickoff is. This preserves
 *     the server's authority to tighten cadence near kickoff.
 *  2. **Inside the imminent window** (`ttk ≤ imminentMs`, or no ttk known) the
 *     delay is the classic `clamp(retryAfter, floor, ceil)`.
 *  3. **Far from kickoff** (`ttk > imminentMs`) the delay is widened to
 *     `min(ttk / 10, sanityFloorMs)`, but **never below** the server's
 *     `Retry-After` and **never longer than** the time remaining to kickoff
 *     (so we don't sleep past it).
 *  4. **Sanity floor caps everything** so an early go-live or a schedule slip
 *     is caught within `sanityFloorMs` (default 5 min).
 *
 * Returns `{ delay, ttkMs }` so the caller can surface `ttk` to UI.
 */
export function nextDelayMs(
  retryAfterMs: number | undefined,
  now: number,
  o: Pick<NormalizedWait, 'floorMs' | 'ceilMs' | 'imminentMs' | 'sanityFloorMs' | 'kickoffMs'>,
  kickoffOverrideMs?: number,
): { delay: number; ttkMs: number | undefined } {
  const kickoffMs = kickoffOverrideMs ?? o.kickoffMs;
  const ttkMs = kickoffMs != null ? Math.max(0, kickoffMs - now) : undefined;
  const serverFloor = Math.max(o.floorMs, retryAfterMs ?? 0);

  // Imminent (or no ttk known): use the classic tight band.
  if (ttkMs == null || ttkMs <= o.imminentMs) {
    return { delay: clamp(retryAfterMs ?? o.floorMs, serverFloor, o.ceilMs), ttkMs };
  }

  // Far from kickoff: widen to ~10% of remaining time, capped by sanity floor
  // and by ttk itself (don't sleep past kickoff).
  const widened = Math.min(Math.floor(ttkMs / 10), o.sanityFloorMs, ttkMs);
  // Server can always tighten with a smaller Retry-After (treated as floor).
  return { delay: Math.max(serverFloor, widened), ttkMs };
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new PlaybackError('ABORTED', 0, 'wait-for-live aborted'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new PlaybackError('ABORTED', 0, 'wait-for-live aborted'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Poll `attempt` until it resolves, retrying on waitable PlaybackErrors
 * (TOO_EARLY / UNAVAILABLE / RATE_LIMITED / NETWORK). Each retry's delay is
 * computed by {@link nextDelayMs} from (a) the server's `Retry-After` (always a
 * floor) and (b) the time-to-kickoff from `kickoffAt` and/or
 * `PlaybackError.liveStartsAtMs`. Far from kickoff the cadence widens (to
 * `~ttk/10`, capped at `sanityFloorMs`) so the SDK does not hammer the
 * per-viewer `/v1/playback` endpoint when a viewer is armed hours early; inside
 * the imminent window it collapses to the tight `[floorMs, ceilMs]` band so a
 * 200 — i.e. the instant the match goes live — is caught fast. Terminal errors
 * (404 / 410 / 400 / 401 / 403 / INTERNAL) reject straight away.
 */
export async function pollToLive<T>(
  attempt: () => Promise<T>,
  cfg: WaitForLive,
  signal?: AbortSignal,
): Promise<T> {
  const o = normalize(cfg);
  const start = Date.now();
  const deadline = o.timeoutMs != null ? start + o.timeoutMs : Infinity;
  let attemptNo = 0;
  // Cache the latest kickoff hint we've learned from a 425 body. Re-read every
  // loop (don't snapshot once) so a schedule change visible to the server is
  // honored — see the four correctness guards in the design note.
  let lastKickoffHintMs: number | undefined;

  for (;;) {
    if (signal?.aborted) throw new PlaybackError('ABORTED', 0, 'wait-for-live aborted');
    try {
      return await attempt();
    } catch (err) {
      if (!(err instanceof PlaybackError) || !isWaitable(err.code)) throw err;
      attemptNo += 1;

      // If the server now carries a kickoff hint (newer havik-streams), refresh
      // it. Caller-supplied `kickoffAt` still wins (set in normalize()).
      if (err.liveStartsAtMs != null && Number.isFinite(err.liveStartsAtMs)) {
        lastKickoffHintMs = err.liveStartsAtMs;
      }

      const now = Date.now();
      const computed = nextDelayMs(err.retryAfterMs, now, o, lastKickoffHintMs);
      const ttkMs = computed.ttkMs;
      let delay = computed.delay;
      if (o.jitter) delay = addJitter(delay);

      if (now + delay > deadline) {
        throw new PlaybackError(
          'TIMEOUT',
          err.httpStatus,
          `timed out after ${o.timeoutMs}ms waiting for the stream to go live`,
          { retryAfterMs: err.retryAfterMs, cause: err },
        );
      }

      o.onState?.({
        phase: PHASE[err.code] ?? 'unavailable',
        retryInMs: delay,
        attempt: attemptNo,
        elapsedMs: now - start,
        ttkMs,
      });

      await sleep(delay, signal);
    }
  }
}
